import React, {useContext, useEffect, useState} from 'react';
import './Pge.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {ProjectsContext} from '../../contexts/projectsContext';
import {AdminContext} from '../../contexts/adminContext';
import {SelectionsContext} from '../../contexts/selectionsContext';
import {Button, Loader, Modal, SelectInput} from '../_common';
import ProjectHeaderItem from './ProjectHeaderItem';
import PgeProjectRow from './PgeProjectRow';
import PgeProjectForm from './PgeProjectForm';
import PgeFilters from './PgeFilters';
import ConfirmationModal from '../_common/ConfirmationModal';
import FullPageLoader from '../_common/FullPageLoader';
import EditSelectedProjectsModal from './EditSelectedProjectsModal';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import FilterListIcon from '@mui/icons-material/FilterList';

const Pge = ({
  recordsPerPage, 
  changeRecordsPerPage, 
  toggleArchiveBackground, 
  urlProjectId, 
  onUrlProjectClose, 
  urlDateFilters = {}, 
  onUrlDateFiltersClear
}) => {
  const { t } = useContext(I18nContext);
  const {
    pending,
    exportPending,
    pgeProjects,
    pgePaginationInfo,
    getPgeProjects,
    exportPgeProjects,
    getSinglePgeProject,
    addPgeProject,
    addProjectPending,
    updatePgeProject,
    deletePgeProject,
    updatePgeProjectState
  } = useContext(ProjectsContext);
  const { usersForDropdowns, getUsersForDropdowns } = useContext(AdminContext);
  const { getSelections, selections } = useContext(SelectionsContext);
  const [page, setPage] = useState(0);
  const [jumpToPageInput, setJumpToPageInput] = useState('');
  const [sort, setSort] = useState({sortField: 'dueDate', order: 'ASC'});
  const [filters, setFilters] = useState(urlDateFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showPreviewProjectModal, setShowPreviewProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [dataEdited, setDataEdited] = useState(false);
  const [showDataEditedWarning, setShowDataEditedWarning] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showEditSelectedProjectsModal, setShowEditSelectedProjectsModal] = useState(false);

  const recordsPerPageOptions = [
    {id: 10, label: 10},
    {id: 25, label: 25},
    {id: 50, label: 50},
    {id: 100, label: 100},
  ];

  useEffect(() => {
    getUsersForDropdowns();
    getSelections();
  }, []);

  useEffect(() => {
    if (Object.keys(urlDateFilters).length > 0) {
      setFilters(urlDateFilters);
      setPage(0);
    }
  }, [JSON.stringify(urlDateFilters)]);

  useEffect(() => {
    if (!!pgePaginationInfo && pgePaginationInfo.number !== page) {
      setPage(pgePaginationInfo.number);
    }
  }, [pgePaginationInfo]);

  useEffect(() => {
    if (page || page === 0) {
      getPgeProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
    }
  }, [page, sort, filters, showArchivedProjects]);

  useEffect(() => {
    setPage(0);
    getPgeProjects(recordsPerPage, 0, sort, filters, showArchivedProjects);
  }, [recordsPerPage]);

  const changeNumberOfRecordsPerPage = (name, number) => {
    changeRecordsPerPage(number);
  };

  const changePage = () => {
    if (
      jumpToPageInput !== pgePaginationInfo.number + 1 &&
      jumpToPageInput <= pgePaginationInfo.totalPages &&
      jumpToPageInput > 0)
    {
      setPage(jumpToPageInput - 1);
      setJumpToPageInput('');
    }
  };

  const handleChangePageOnEnterPress = e => {
    if (e.keyCode === 13) {
      changePage();
    }
  };

  const changePageUp = () => {
    if (page < pgePaginationInfo.totalPages - 1) {
      setPage(page + 1);
    }
  };

  const changePageDown = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const changePageFirst = () => {
    setPage(0);
  };

  const changePageLast = () => {
    setPage(pgePaginationInfo.totalPages - 1);
  };

  const applySort = (sortField, order) => {
    setSort({sortField, order});
  };

  const toggleShowFilters = () => {
    setShowFilters(!showFilters);
  };

  const applyFilters = filters => {
    setFilters(filters);
    setPage(0);
    if (!filters.fromLastModificationDate && !filters.toLastModificationDate) {
      onUrlDateFiltersClear?.();
    }
  };

  const toggleShowAddProjectModal = () => {
    if (showAddProjectModal && dataEdited) {
      setShowDataEditedWarning(true);
    } else {
      setShowAddProjectModal(!showAddProjectModal);
    }
  };

  const addNewProject = async payload => {
    setDataEdited(false);
    const newProject = await addPgeProject(payload);
    setShowAddProjectModal(false);
    getPgeProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
    return newProject;
  };

  const openEditProjectModal = async id => {
    const project = await getSinglePgeProject(id);
    if (project) {
      setSelectedProject(project);
      setShowEditProjectModal(true);
    }
  };

  const closeEditProjectModal = noCheckForEditedData => {
    if (dataEdited && !noCheckForEditedData) {
      setShowDataEditedWarning(true);
    } else {
      setShowEditProjectModal(false);
      setSelectedProject(null);
      onUrlProjectClose?.();
    }
    getPgeProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
  };

  const editProject = async (payload, projectId, version, noClose) => {
    setDataEdited(false);
    const updatedProject = await updatePgeProject(payload, projectId, version);
    if (!noClose) {
      setShowEditProjectModal(false);
      setSelectedProject(null);
      getPgeProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
      onUrlProjectClose?.();
    }
    return updatedProject;
  };

  const openDeleteProjectModal = async id => {
    setSelectedProject(await getSinglePgeProject(id));
    setShowDeleteProjectModal(true);
  };

  const closeDeleteProjectModal = () => {
    setShowDeleteProjectModal(false);
    setSelectedProject(null);
  };

  const deleteProject = async id => {
    const deletedProject = await deletePgeProject(id);
    setShowDeleteProjectModal(false);
    setSelectedProject(null);
    getPgeProjects(recordsPerPage, 0, sort, filters, showArchivedProjects);
  };

  const refreshProjectsList = () => {
    getPgeProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
  };

  const openPreviewProjectModal = async id => {
    const project = await getSinglePgeProject(id);
    if (project) {
      setSelectedProject(project);
      setShowPreviewProjectModal(true);
    }
  };

  const closePreviewProjectModal = () => {
    setShowPreviewProjectModal(false);
    setSelectedProject(null);
    getPgeProjects(recordsPerPage, page, sort, filters, showArchivedProjects);
  };

  useEffect(() => {
    if (urlProjectId) {
      openEditProjectModal(urlProjectId);
    }
  }, [urlProjectId]);

  const finishProject = async projectId => {
    await updatePgeProjectState('completed', projectId, t('finished'));
    refreshProjectsList();
  };

  const cancelProject = async projectId => {
    await updatePgeProjectState('canceled', projectId, t('canceled'));
    refreshProjectsList();
  };

  const reactivateProject = async (projectId, previousState) => {
    await updatePgeProjectState(previousState, projectId, t('reactivated'));
    refreshProjectsList();
  };

  const toggleShowArchivedProjects = () => {
    setShowArchivedProjects(!showArchivedProjects);
    toggleArchiveBackground();
  };

  const confirmCloseProjectWithEditedData = () => {
    setDataEdited(false);
    if (showAddProjectModal) {
      setShowAddProjectModal(false);
    }
    if (showEditProjectModal) {
      setShowEditProjectModal(false);
      setSelectedProject(null);
      onUrlProjectClose?.();
    }
    setShowDataEditedWarning(false);
  };

  const refreshSelectedProject = project => {
    setSelectedProject(project);
  };

  const exportProjectsToExcel = () => {
    exportPgeProjects(recordsPerPage, page, sort, filters, showArchivedProjects)
  };

  const toggleSelectProject = id => {
    const newSelectedProjects = [...selectedProjects];
    const index = selectedProjects.indexOf(id);

    if (newSelectedProjects.includes(id)) {
      newSelectedProjects.splice(index, 1)
    } else {
      newSelectedProjects.push(id);
    }

    setSelectedProjects(newSelectedProjects);
  };

  const openEditMultipleProjectsModal = async () => {
    setShowEditSelectedProjectsModal(true);
  };

  const closeEditMultipleProjectsModal = () => {
    setShowEditSelectedProjectsModal(false);
  };

  const clearProjectsSelection = () => {
    setSelectedProjects([]);
  };

  const checkIfAllProjectsSelected = () => {
    let allSelected = true;
    pgeProjects.map(project => project.id).forEach(id => {
      if (!selectedProjects.includes(id)) {
        allSelected = false;
      }
    })
    return allSelected;
  };

  const toggleSelectAllProjects = () => {
    let newSelectedProjects = [...selectedProjects];

    if (checkIfAllProjectsSelected()) {
      pgeProjects.map(project => project.id).forEach(id => {
        if (newSelectedProjects.includes(id)) {
          const index = newSelectedProjects.indexOf(id);
          newSelectedProjects.splice(index, 1);
        }
      })
    } else {
      pgeProjects.map(project => project.id).forEach(id => {
        if (!newSelectedProjects.includes(id)) {
          newSelectedProjects.push(id);
        }
      })
    }

    setSelectedProjects(newSelectedProjects)
  };

  const toggleMarkProjectAsUrgent = async (id, value) => {
    const projectObj = await getSinglePgeProject(id);
    await updatePgeProject({urgent: value}, id, projectObj?.version, true);
    refreshProjectsList();
  };

  const toggleMarkProjectAsToBePlanned = async (id, value) => {
    const projectObj = await getSinglePgeProject(id);
    await updatePgeProject({toBePlanned: value}, id, projectObj?.version, true);
    refreshProjectsList();
  };

  return (
    <div className='Pge'>
      <div className='header'>
        <div className='filters'>
          <PgeFilters
            filters={filters}
            showFilters={showFilters}
            toggleShowFilters={toggleShowFilters}
            setFilters={applyFilters}
            selections={selections}
            usersForDropdowns={usersForDropdowns}
            archived={showArchivedProjects}
          />
        </div>
        <div className='filtersMobile'>
          <div className='filtersButton' onClick={toggleShowFilters}>
            <FilterListIcon />
            <div className='filtersButtonText'>
              {t('filters')}
            </div>
          </div>
          {showFilters && (
            <PgeFilters
              filters={filters}
              showFilters={showFilters}
              toggleShowFilters={toggleShowFilters}
              setFilters={applyFilters}
              selections={selections}
              usersForDropdowns={usersForDropdowns}
            />
          )}
        </div>
        <div className='headerButtons'>
          <div className='buttonsGroup'>
            <Button
              label={t('export')}
              action={exportProjectsToExcel}
              pending={exportPending}
            />
            <Button
              type='secondary'
              label={`${t('edit selected')} (${selectedProjects.length})`}
              action={openEditMultipleProjectsModal}
              disabled={!selectedProjects.length}
            />
            {!!selectedProjects.length && (
              <Button
                type='secondary'
                label={t('clear selection')}
                action={clearProjectsSelection}
                disabled={!selectedProjects.length}
              />
            )}
          </div>
          <div className='buttonsGroup'>
            <Button
              label={!showArchivedProjects ? t('go to archived projects') : t('go to ongoing projects')}
              action={toggleShowArchivedProjects}
            />
            <Button type='secondary' label={t('add project')} action={toggleShowAddProjectModal} />
          </div>
        </div>
      </div>
      <div className='headerTitle'>
        {!showArchivedProjects ? t('active projects') : t('archived projects')}
        {' '}
        {`(${t('found', 'lower')} ${pgePaginationInfo.totalRecords})`}
      </div>
      <div className='projectsTable'>
        {!pending && !!pgePaginationInfo && !!pgePaginationInfo?.totalPages && (
          <div className='paginationRow'>
            <div className='paginationRowItem'>
              <SelectInput
                label={t('records per page')}
                options={recordsPerPageOptions}
                onSelect={changeNumberOfRecordsPerPage}
                value={recordsPerPage}
                small
                inline
                noMargin
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`paginationButton ${pgePaginationInfo.totalPages === 1 || !jumpToPageInput.length
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePage}
              >
                {t('go to page')}
              </div>
              <input
                type='number'
                className={`paginationInput ${pgePaginationInfo.totalPages === 1 ? 'paginationInputDisabled' : ''}`}
                onChange={e => setJumpToPageInput(e.target.value)}
                onKeyDown={handleChangePageOnEnterPress}
                disabled={pgePaginationInfo.totalPages === 1}
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginRight
                  ${pgePaginationInfo.number === 0 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageFirst}
              >
                <FirstPageIcon />
              </div>
              <div
                className={`paginationButton ${pgePaginationInfo.number === 0 ? 'paginationDisabled' : ''}`}
                onClick={changePageDown}
              >
                <NavigateBeforeIcon />
              </div>
              <div className='paginationInfo'>
                {t('page')} {pgePaginationInfo.number + 1}/{pgePaginationInfo.totalPages}
              </div>
              <div
                className={`paginationButton ${pgePaginationInfo.number === pgePaginationInfo.totalPages - 1
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePageUp}
              >
                <NavigateNextIcon />
              </div>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginLeft 
                  ${pgePaginationInfo.number === pgePaginationInfo.totalPages - 1 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageLast}
              >
                <LastPageIcon />
              </div>
            </div>
          </div>
        )}

        <div className='projectsTableHeader'>
          <div className='headerItemSelect'>
            <input
              className='selectAllProjectsCheckbox'
              type='checkbox'
              checked={checkIfAllProjectsSelected()}
              onChange={toggleSelectAllProjects}
            />
          </div>
          <div className='headerItemMeatball'/>
          <div className='headerItemUrgentMark'/>
          <div className='headerItemToBePlannedMark'/>
          <div className='ordinalNumber'>#</div>
          <ProjectHeaderItem
            label={t('investment address')}
            fieldName='investmentAddress'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('order number')}
            fieldName='orderNumber'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('conditions number')}
            fieldName='conditionsNumber'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('framework agreement number')}
            fieldName='frameworkAgreementNumber'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('pge region', 'asIs')}
            fieldName='pgeRegionId'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('person responsible for the project')}
            fieldName='personResponsibleForProjectId'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('person responsible for the implementation')}
            fieldName='personResponsibleForImplementationId'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('client name')}
            fieldName='clientFullName'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('due date')}
            fieldName='dueDate'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('order type')}
            fieldName='orderType'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('created at')}
            fieldName='createdAt'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('last modified')}
            fieldName='lastModificationDate'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('order cost')}
            fieldName='cost'
            applySort={applySort}
            sort={sort}
          />
          <ProjectHeaderItem
            label={t('project state')}
            fieldName='projectState'
            applySort={applySort}
            sort={sort}
          />
        </div>

        {!!pending && (
          <div className='loaderWrapper'><Loader /></div>
        )}

        {!pending && !pgeProjects.length && (
            <div className='noProjectsInfo'>
              {t('no projects meeting the criteria')}
            </div>
        )}

        {!pending && !!usersForDropdowns.length && pgeProjects.map((project, i) => (
          <PgeProjectRow
            key={project.id}
            project={project}
            users={usersForDropdowns}
            openEditProjectModal={() => openEditProjectModal(project.id)}
            openDeleteProjectModal={() => openDeleteProjectModal(project.id)}
            openPreviewProjectModal={() => openPreviewProjectModal(project.id)}
            finishProject={finishProject}
            cancelProject={cancelProject}
            reactivateProject={reactivateProject}
            selections={selections}
            ordinalNumber={i + 1}
            toggleSelectProject={toggleSelectProject}
            isSelected={selectedProjects.includes(project.id)}
            toggleMarkProjectAsUrgent={toggleMarkProjectAsUrgent}
            toggleMarkProjectAsToBePlanned={toggleMarkProjectAsToBePlanned}
          />
        ))}

        {!pending && !!pgePaginationInfo && !!pgePaginationInfo?.totalPages && (
          <div className='paginationRow'>
            <div className='paginationRowItem'>
              <SelectInput
                label={t('records per page')}
                options={recordsPerPageOptions}
                onSelect={changeNumberOfRecordsPerPage}
                value={recordsPerPage}
                small
                inline
                noMargin
                dropdownOnTop
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`paginationButton ${pgePaginationInfo.totalPages === 1 || !jumpToPageInput.length
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePage}
              >
                {t('go to page')}
              </div>
              <input
                type='number'
                className={`paginationInput ${pgePaginationInfo.totalPages === 1 ? 'paginationInputDisabled' : ''}`}
                onChange={e => setJumpToPageInput(e.target.value)}
                onKeyDown={handleChangePageOnEnterPress}
                disabled={pgePaginationInfo.totalPages === 1}
              />
            </div>

            <div className='paginationRowItem'>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginRight
                  ${pgePaginationInfo.number === 0 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageFirst}
              >
                <FirstPageIcon />
              </div>
              <div
                className={`paginationButton ${pgePaginationInfo.number === 0 ? 'paginationDisabled' : ''}`}
                onClick={changePageDown}
              >
                <NavigateBeforeIcon />
              </div>
              <div className='paginationInfo'>
                {t('page')} {pgePaginationInfo.number + 1}/{pgePaginationInfo.totalPages}
              </div>
              <div
                className={`paginationButton ${pgePaginationInfo.number === pgePaginationInfo.totalPages - 1
                  ? 'paginationDisabled'
                  : ''
                }`}
                onClick={changePageUp}
              >
                <NavigateNextIcon />
              </div>
              <div
                className={`
                  paginationButton 
                  paginationButton--marginLeft 
                  ${pgePaginationInfo.number === pgePaginationInfo.totalPages - 1 ? 'paginationDisabled' : ''}
                `}
                onClick={changePageLast}
              >
                <LastPageIcon />
              </div>
            </div>
          </div>
        )}
      </div>

      {!!showAddProjectModal && (
        <Modal
          closeModal={toggleShowAddProjectModal}
          title={t('add project')}
          width='99%'
          height='760px'
        >
          <PgeProjectForm
            users={usersForDropdowns}
            primaryButtonAction={addNewProject}
            secondaryButtonAction={toggleShowAddProjectModal}
            setDataAsEdited={() => setDataEdited(true)}
          />
        </Modal>
      )}

      {!!showEditProjectModal && (
        <Modal
          closeModal={closeEditProjectModal}
          title={t('edit project')}
          width='99%'
          height='99%'
        >
          <PgeProjectForm
            project={selectedProject}
            users={usersForDropdowns}
            primaryButtonAction={editProject}
            secondaryButtonAction={closeEditProjectModal}
            refreshProjectsList={refreshProjectsList}
            setDataAsEdited={() => setDataEdited(true)}
            refreshSelectedProject={refreshSelectedProject}
          />
        </Modal>
      )}

      {!!showPreviewProjectModal && (
        <Modal
          closeModal={closePreviewProjectModal}
          title={t('preview project')}
          width='99%'
          height='99%'
        >
          <PgeProjectForm
            project={selectedProject}
            users={usersForDropdowns}
            secondaryButtonAction={closePreviewProjectModal}
            refreshProjectsList={refreshProjectsList}
            preview
          />
        </Modal>
      )}

      {!!showDataEditedWarning && (
        <ConfirmationModal
          modalText={t('are you sure you want to close the project form and loose unsaved changes')}
          closeModal={() => setShowDataEditedWarning(false)}
          title={t('data has been edited')}
          itemName=''
          primaryButtonAction={confirmCloseProjectWithEditedData}
          primaryButtonLabel={t('close project')}
        />
      )}

      {!!showDeleteProjectModal && (
        <ConfirmationModal
          modalText={t('are you sure you want to delete the project')}
          closeModal={closeDeleteProjectModal}
          title={t('delete project')}
          itemName={selectedProject.investmentAddress}
          primaryButtonAction={() => deleteProject(selectedProject.id)}
          primaryButtonLabel={t('delete project')}
        />
      )}

      {!!showEditSelectedProjectsModal && (
        <EditSelectedProjectsModal
          selectedProjects={selectedProjects}
          closeModal={closeEditMultipleProjectsModal}
          refreshProjectsList={refreshProjectsList}
          clearProjectsSelection={clearProjectsSelection}
        />
      )}

      {!!addProjectPending && (
        <FullPageLoader />
      )}
    </div>
  );
};

export default Pge;
